import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { formatCurrency } from '../../utils/helpers';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import { t } from '../../i18n';

const SEVERITY_TONE = {
  Alta: 'danger',
  Media: 'warning',
  Baja: 'success'
};

const RISK_STATUS_TONE = {
  abierto: 'warning',
  mitigado: 'info',
  cerrado: 'success'
};

const ProjectRisks = ({ embedded = false, parkId = '' }) => {
  const { renewablePortfolio, renewableRisks, updateRenewableRisk, preferences, updatePreferences } = useAppContext();
  const preferredParkId = parkId || preferences?.projectContext?.parkId || 'all';
  const defaultProjectId = useMemo(() => {
    if (!renewablePortfolio || renewablePortfolio.length === 0) return '';
    if (preferredParkId !== 'all') {
      const match = renewablePortfolio.find((project) => project.parkId === preferredParkId);
      if (match) return match.id;
    }
    return renewablePortfolio[0]?.id || '';
  }, [renewablePortfolio, preferredParkId]);
  const [selectedProjectId, setSelectedProjectId] = useState(defaultProjectId);

  const selectedProject = useMemo(
    () => renewablePortfolio.find((project) => project.id === selectedProjectId) || null,
    [renewablePortfolio, selectedProjectId]
  );

  useEffect(() => {
    if (!defaultProjectId) return;
    setSelectedProjectId(defaultProjectId);
  }, [defaultProjectId]);

  useEffect(() => {
    const parkId = selectedProject?.parkId || null;
    if (!parkId) return;
    updatePreferences({ projectContext: { parkId } });
  }, [selectedProject?.parkId, updatePreferences]);

  const riskData = useMemo(
    () => renewableRisks.filter((risk) => risk.projectId === selectedProjectId),
    [renewableRisks, selectedProjectId]
  );

  const tableData = useMemo(
    () =>
      riskData.map((risk) => ({
        ...risk,
        probabilityLabel: `${Math.round(risk.probability * 100)}%`,
        capexImpactLabel: formatCurrency(risk.capexImpact || 0),
        daysImpactLabel: `${risk.daysImpact ?? 0} días`
      })),
    [riskData]
  );

  const columns = useMemo(
    () => [
      {
        key: 'title',
        label: t('Riesgo'),
        sortable: true,
        render: (value) => (
          <span className="inline-flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-neutral-400" />
            <span className="font-medium text-neutral-800">{t(value)}</span>
          </span>
        )
      },
      {
        key: 'severity',
        label: t('Severidad'),
        sortable: true,
        render: (value) => <Badge tone={SEVERITY_TONE[value] || 'neutral'}>{t(value)}</Badge>
      },
      { key: 'probabilityLabel', label: t('Probabilidad'), align: 'right', sortable: true },
      { key: 'owner', label: t('Responsable'), sortable: true },
      { key: 'capexImpactLabel', label: t('Impacto CAPEX'), align: 'right', sortable: true },
      { key: 'daysImpactLabel', label: t('Impacto plazo'), align: 'right', sortable: true },
      { key: 'mitigation', label: t('Mitigación') },
      {
        key: 'status',
        label: t('Estado'),
        render: (value, row) => (
          <span className="inline-flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
            <Badge tone={RISK_STATUS_TONE[value] || 'neutral'}>{t(value)}</Badge>
            <select
              value={value}
              onChange={(event) => updateRenewableRisk(row.id, { status: event.target.value })}
              className="text-xs border border-slate-200 rounded px-2 py-1"
            >
              <option value="abierto">{t('Abierto')}</option>
              <option value="mitigado">{t('Mitigado')}</option>
              <option value="cerrado">{t('Cerrado')}</option>
            </select>
          </span>
        )
      }
    ],
    [updateRenewableRisk]
  );

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{t('Avance y Riesgos')}</h1>
            <p className="text-sm text-slate-500">{t('Monitoreo de riesgos, impacto en CAPEX/plazo y plan de mitigacion.')}</p>
          </div>
          <select
            value={selectedProjectId}
            onChange={(event) => setSelectedProjectId(event.target.value)}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm"
          >
            {renewablePortfolio.map((project) => (
              <option key={project.id} value={project.id}>
                {t(project.name)}
              </option>
            ))}
          </select>
        </div>
      )}

      <DataTable
        data={tableData}
        columns={columns}
        searchPlaceholder={t('Buscar riesgos...')}
        emptyTitle={t('Sin riesgos registrados')}
        emptyDescription={t('No hay riesgos asociados a este proyecto.')}
      />
    </div>
  );
};

export default ProjectRisks;
